import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export const TERMS_LAST_UPDATED = 'July 12, 2026';

function Terms() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="legal-logo">
            around
          </Link>
          <h1>Terms of Service</h1>
          <p className="legal-updated">Last updated: {TERMS_LAST_UPDATED}</p>
        </header>

        <section>
          <h2>1. Acceptance of These Terms</h2>
          <p>
            These Terms of Service (the &ldquo;Terms&rdquo;) are a binding agreement between you
            and Around (&ldquo;Around,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;) governing your use of the Around website, applications, and
            related services (collectively, the &ldquo;Service&rdquo;). By creating an account,
            clicking to accept these Terms, or using the Service, you agree to be bound by these
            Terms and by our <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not
            use the Service.
          </p>
        </section>

        <section>
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 13 years old to use the Service. If you are under 18, you may use
            the Service only with the consent and supervision of a parent or legal guardian who
            agrees to these Terms on your behalf. By using the Service, you represent that you meet
            these requirements and that any registration information you provide is accurate.
          </p>
        </section>

        <section>
          <h2>3. Your Account</h2>
          <p>
            You are responsible for your account and everything that happens under it. Keep your
            password confidential, and notify us promptly if you suspect unauthorized access. You
            may not share, sell, or transfer your account, create an account for anyone other than
            yourself, or impersonate another person. We may suspend or terminate accounts that
            violate these Terms.
          </p>
        </section>

        <section>
          <h2>4. The Service</h2>
          <p>
            Around helps golfers find playing partners, connect with other golfers, exchange
            messages, share activity, coordinate rounds, and post or fill open tee-time spots. We
            may add, change, or remove features at any time. We do not guarantee that any
            particular feature will remain available.
          </p>
        </section>

        <section>
          <h2>5. Your Content</h2>
          <p>
            You retain ownership of the content you post to the Service — your profile information,
            photos, posts, comments, and messages (&ldquo;User Content&rdquo;). By posting User
            Content, you grant Around a worldwide, non-exclusive, royalty-free license to
            host, store, display, reproduce, and distribute that content solely as needed to
            operate, provide, and improve the Service. This license ends when you delete the
            content or your account, except where content has been shared with others (for
            example, messages you sent) or where retention is required by law.
          </p>
          <p>
            You are solely responsible for your User Content. You represent that you have the
            rights to post it and that it does not violate any law or the rights of any third
            party. We may remove any User Content that violates these Terms or that we reasonably
            believe is harmful to the Service or its users.
          </p>
        </section>

        <section>
          <h2>6. Acceptable Use</h2>
          <p>When using the Service, you agree that you will not:</p>
          <ul>
            <li>harass, threaten, defame, or abuse other users;</li>
            <li>post content that is unlawful, obscene, hateful, or deceptive;</li>
            <li>
              misrepresent your identity, handicap, GHIN status, or any other information about
              yourself;
            </li>
            <li>use the Service to send spam or unsolicited commercial messages;</li>
            <li>
              collect, harvest, or disclose other users&rsquo; personal information without their
              consent;
            </li>
            <li>
              upload viruses or malicious code, probe or test the vulnerability of the Service, or
              interfere with its operation or security;
            </li>
            <li>
              access the Service by automated means (bots, scrapers, crawlers) or circumvent any
              access restrictions or rate limits; or
            </li>
            <li>use the Service for any purpose that violates applicable law.</li>
          </ul>
        </section>

        <section>
          <h2>7. Our Intellectual Property; License and Restrictions</h2>
          <p>
            <strong>7.1 Ownership.</strong> The Service — including its software and source code,
            visual design, user interface, graphics, logos, trademarks, text, and the selection,
            arrangement, and coordination of its features and functionality (collectively, the
            &ldquo;Around Materials&rdquo;) — is owned by Around and is protected by
            copyright, trademark, trade dress, trade secret, and other intellectual-property laws.
            Except for the limited license below, nothing in these Terms transfers any right,
            title, or interest in the Around Materials to you. The &ldquo;Around&rdquo;
            name and logo may not be used without our prior written permission.
          </p>
          <p>
            <strong>7.2 Limited License.</strong> We grant you a limited, non-exclusive,
            non-transferable, non-sublicensable, revocable license to access and use the Service
            for your personal, non-commercial use in accordance with these Terms.
          </p>
          <p>
            <strong>7.3 Restrictions.</strong> Except as expressly permitted by these Terms or by
            applicable law notwithstanding a contractual restriction, you may not, and may not
            enable, permit, or assist any third party to:
          </p>
          <ul>
            <li>
              copy, modify, translate, adapt, or create derivative works of the Service or the
              Around Materials;
            </li>
            <li>
              reverse engineer, decompile, disassemble, or otherwise attempt to derive the source
              code, architecture, algorithms, or non-public structure of the Service;
            </li>
            <li>
              scrape, crawl, harvest, or extract data, profiles, or content from the Service, or
              use any data obtained from the Service to build, train, populate, or improve any
              other product, service, or database;
            </li>
            <li>
              access or use the Service, or any information obtained from it, for the purpose of
              building, assisting in the development of, or operating a product or service that
              competes with the Service, or for the purpose of copying its features, functions,
              workflows, or user interface;
            </li>
            <li>
              frame, mirror, republish, rent, lease, sell, sublicense, or otherwise commercially
              exploit the Service;
            </li>
            <li>
              monitor, benchmark, or analyze the Service&rsquo;s availability, performance, or
              functionality for any competitive purpose; or
            </li>
            <li>remove, obscure, or alter any proprietary rights notices.</li>
          </ul>
          <p>
            <strong>7.4 Enforcement.</strong> Any violation of this Section 7 is a material breach
            of these Terms. We may suspend or terminate your access immediately and without notice,
            and we reserve all rights and remedies available at law and in equity, including
            damages and injunctive relief. You agree that a breach of this Section may cause us
            irreparable harm for which monetary damages would be inadequate, and that we are
            entitled to seek injunctive relief without the requirement of posting a bond.
          </p>
          <p>
            <strong>7.5 Feedback.</strong> If you send us ideas, suggestions, or feedback about the
            Service, you grant us the right to use them without restriction or compensation to you.
          </p>
        </section>

        <section>
          <h2>8. Playing Golf with Other Users; Assumption of Risk</h2>
          <p>
            Around helps you meet other golfers, but we do not screen users, run background
            checks, verify identities (beyond optional GHIN verification of handicap data), or
            supervise rounds, meetups, or any other in-person interaction. When you choose to meet
            or play golf with someone you found through the Service, you do so at your own risk.
            You are solely responsible for your interactions with other users, both online and in
            person. Use good judgment: meet in public places such as golf courses, and tell someone
            where you are going.
          </p>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, GOLFCONNECT IS NOT RESPONSIBLE FOR THE CONDUCT
            OF ANY USER, WHETHER ONLINE OR OFFLINE, OR FOR ANY INJURY, LOSS, OR DAMAGE ARISING FROM
            ANY MEETING, ROUND, OR OTHER INTERACTION BETWEEN USERS.
          </p>
        </section>

        <section>
          <h2>9. Tee Time Marketplace</h2>
          <p>
            The marketplace lets users post open tee-time spots and requests for playing partners.
            Around is not a party to any arrangement made between users, does not book tee
            times, does not process payments between users, and makes no representations about any
            tee time, course, price, or user. Any arrangement — including any payment or
            reimbursement between users — is solely between the users involved.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services</h2>
          <p>
            The Service may display information from or link to third-party services, such as GHIN
            handicap data or golf course information. We do not control and are not responsible for
            third-party services, and displaying such information is not an endorsement. Your use
            of a third-party service is governed by that service&rsquo;s own terms.
          </p>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>
            You may stop using the Service at any time and may request deletion of your account by
            contacting us. We may suspend or terminate your access to the Service at any time, with
            or without notice, if we reasonably believe you have violated these Terms, if required
            by law, or if we discontinue the Service. Sections 5 (for content already shared), 7,
            8, 12, 13, 14, and 15 survive termination.
          </p>
        </section>

        <section>
          <h2>12. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
            THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE
            CORRECTED, OR THAT ANY INFORMATION ON THE SERVICE (INCLUDING HANDICAP OR PROFILE DATA
            PROVIDED BY USERS) IS ACCURATE.
          </p>
        </section>

        <section>
          <h2>13. Limitation of Liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, GOLFCONNECT AND ITS OWNERS, OFFICERS,
            EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, DATA,
            GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF THE
            SERVICE. TO THE FULLEST EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS
            RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS
            (US $100) OR THE AMOUNT YOU PAID US, IF ANY, IN THE TWELVE MONTHS BEFORE THE CLAIM
            AROSE. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THE ABOVE MAY
            NOT APPLY TO YOU.
          </p>
        </section>

        <section>
          <h2>14. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Around and its owners, officers,
            employees, and agents from any claims, liabilities, damages, losses, and expenses
            (including reasonable attorneys&rsquo; fees) arising out of or related to your User
            Content, your use of the Service, your interactions with other users, or your violation
            of these Terms or of any law or third-party right.
          </p>
        </section>

        <section>
          <h2>15. Governing Law and Disputes</h2>
          <p>
            These Terms are governed by the laws of the State of Florida, without regard to
            its conflict-of-laws rules. Any dispute arising out of or relating to these Terms or
            the Service that cannot be resolved informally will be brought exclusively in the state
            or federal courts located in Florida, and you consent to their jurisdiction.
            Before filing any claim, you agree to first contact us and attempt in good faith to
            resolve the dispute informally for at least thirty (30) days.
          </p>
        </section>

        <section>
          <h2>16. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make material changes, we will
            notify you — for example, by asking you to review and accept the updated Terms the next
            time you sign in. The &ldquo;Last updated&rdquo; date above reflects the current
            version. Your continued use of the Service after changes take effect constitutes
            acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2>17. Contact</h2>
          <p>
            Questions about these Terms? Contact us at{' '}
            <a href="mailto:dkoski1995@gmail.com">dkoski1995@gmail.com</a>.
          </p>
        </section>

        <footer className="legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/">Back to around</Link>
        </footer>
      </div>
    </div>
  );
}

export default Terms;
